import { Props_SnippetAccordion } from "./Accordion.props";
import SnippetLink from '../Shared/Link/Link';
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CompileHeadingToSpan, CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'

const ANIMATION_DURATION = 0.3;

type AccordionRowProps = {
    accordionRow: Props_SnippetAccordion['repeatable_accordionRow'][number];
    index: number;
    isOpen: boolean;
    isDetailsOpen: boolean;
    onToggle: (index: number, event: React.MouseEvent) => void;
    onExitComplete: (index: number) => void;
};

const AccordionRow = ({ accordionRow, index, isOpen, isDetailsOpen, onToggle, onExitComplete }: AccordionRowProps) => {
    const { linkText, linkHref, linkStyle, linkTarget, title, text } = accordionRow;

    return (
        <details
            key={"accordion-row-" + index}
            className={`snippet-accordion__row ${isOpen ? "snippet-accordion__row--open" : ""}`}
            open={isDetailsOpen}
        >
            <summary
                className="snippet-accordion__row-title"
                onClick={(e) => onToggle(index, e)}
            >
                {title?.value ? title.value : "Accordion Row " + (index + 1)}
            </summary>
            <AnimatePresence initial={false} onExitComplete={() => onExitComplete(index)}>
                {isOpen && (
                    <motion.div
                        className="snippet-accordion__content-container"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: ANIMATION_DURATION, ease: "easeInOut" }}
                    >
                        {text && (
                            <p className="snippet-accordion__text">{text.value}</p>
                        )}
                        {linkHref?.value && linkText?.value && (
                            <SnippetLink
                                linkStyles={linkStyle?.value}
                                className="snippet-accordion__link"
                                url={linkHref?.value}
                                openInNewTab={!!linkTarget?.value}
                                rel={linkTarget ? "noopener noreferrer" : undefined}
                            >
                                {linkText?.value}
                            </SnippetLink>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </details>
    );
};

const SnippetAccordion = (props: Props_SnippetAccordion) => {
    const { accordionTitle, repeatable_accordionRow, eyebrow, summary } = props;
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [detailsOpenSet, setDetailsOpenSet] = useState<Set<number>>(new Set());
    const accordionTitleClass = accordionTitle.visibility?.value ? "" : "accessible-heading";

    const handleToggle = (index: number, event: React.MouseEvent) => {
        event.preventDefault();
        
        if (openIndex === index) {
            setOpenIndex(null);
        } else {
            setOpenIndex(index);
            setDetailsOpenSet(prev => new Set(prev).add(index));
        }
    };

    const handleExitComplete = (index: number) => {
        setDetailsOpenSet(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    return (
        <section className="snippet-accordion">
            <div className="snippet-accordion__container">
              <div className="snippet-accordion__heading-container">
                {accordionTitle?.text?.value && (
                     <h2 className={`snippet-accordion__title ${accordionTitleClass}`}>
                        {CompileHeadingToSpan(accordionTitle.text.value)}
                    </h2>
                )}
                {eyebrow?.text?.value && (
                    <p className="snippet-accordion__eyebrow">
                        {eyebrow.text.value}
                    </p>
                )}
                {summary?.content?.value && (
                    <div className="snippet-accordion__summary">
                        {CompileMarkdown(summary.content.value)}
                    </div>
                )}</div>

                <div className="snippet-accordion__row-container">
                    {repeatable_accordionRow.map((accordionRow, index) => (
                        <AccordionRow
                            key={"accordion-row-" + index}
                            accordionRow={accordionRow}
                            index={index}
                            isOpen={openIndex === index}
                            isDetailsOpen={detailsOpenSet.has(index)}
                            onToggle={handleToggle}
                            onExitComplete={handleExitComplete}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export { SnippetAccordion };  